import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Toolbar
} from "@mui/material";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";
import EditWrap from "../../commonComponent/editWrap";

export const gaugeTypeOptions = [
  { id: 1, label: "Thread Plug Gauge" },
  { id: 2, label: "Thread Ring Gauge" }
];

export default function EditThreadRingPlug(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id && id !== "0");

  const [gaugeType, setGaugeType] = useState(null);
  const [specification, setSpecification] = useState("");
  const [goMax, setGoMax] = useState("");
  const [goMin, setGoMin] = useState("");
  const [goWearLimit, setGoWearLimit] = useState("");
  const [nogoMax, setNogoMax] = useState("");
  const [nogoMin, setNogoMin] = useState("");
  const [nogoWearLimit, setNogoWearLimit] = useState("");

  useEffect(() => {
    if (isEditing) {
      fetchDetails();
    }
  }, [id]);

  const fetchDetails = async () => {
    try {
      const res = await axiosWithToken.get(`${BASE_URL}ThreadPlugRingSizes/${id}`);
      const data = Array.isArray(res.data) ? res.data[0] : res.data;
      if (data) {
        const foundType = gaugeTypeOptions.find((opt) => opt.id === Number(data.gaugeType)) || null;
        setGaugeType(foundType);
        setSpecification(data.specification || "");
        setGoMax(data.goMax !== null && data.goMax !== undefined ? data.goMax : "");
        setGoMin(data.goMin !== null && data.goMin !== undefined ? data.goMin : "");
        setGoWearLimit(data.goWearLimit !== null && data.goWearLimit !== undefined ? data.goWearLimit : "");
        setNogoMax(data.nogoMax !== null && data.nogoMax !== undefined ? data.nogoMax : "");
        setNogoMin(data.nogoMin !== null && data.nogoMin !== undefined ? data.nogoMin : "");
        setNogoWearLimit(data.nogoWearLimit !== null && data.nogoWearLimit !== undefined ? data.nogoWearLimit : "");
      }
    } catch (error) {
      console.error("Error fetching Thread Plug/Ring details:", error);
      toast.error("Failed to load specification details");
    }
  };

  const handleSubmit = async () => {
    if (!gaugeType) {
      toast.error("Please select a Gauge Type");
      return;
    }
    if (!specification.trim()) {
      toast.error("Please enter a Standard Specification");
      return;
    }

    const payload = {
      gaugeType: gaugeType.id,
      specification: specification.trim(),
      goMax: goMax !== "" ? parseFloat(goMax) : null,
      goMin: goMin !== "" ? parseFloat(goMin) : null,
      goWearLimit: goWearLimit !== "" ? parseFloat(goWearLimit) : null,
      nogoMax: nogoMax !== "" ? parseFloat(nogoMax) : null,
      nogoMin: nogoMin !== "" ? parseFloat(nogoMin) : null,
      nogoWearLimit: nogoWearLimit !== "" ? parseFloat(nogoWearLimit) : null
    };

    try {
      if (isEditing) {
        await axiosWithToken.patch(`${BASE_URL}ThreadPlugRingSizes/${id}`, payload);
        toast.success("Thread Ring/Plug updated successfully!");
      } else {
        await axiosWithToken.post(`${BASE_URL}ThreadPlugRingSizes`, payload);
        toast.success("Thread Ring/Plug created successfully!");
      }
      setTimeout(() => {
        navigate("/master/threadRingPlugList");
      }, 500);
    } catch (error) {
      console.error("Error saving Thread Plug/Ring:", error);
      toast.error("Failed to save ");
    }
  };

  return (
    <EditWrap>
      {/* Page Title */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
        <Typography variant="h6" component="h6" style={{ float: "left" }}>
          {isEditing ? "Edit Thread Ring/Plug " : "Create New Thread Ring/Plug"}
        </Typography>
      </div>

      {/* Form Controls */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            size="small"
            options={gaugeTypeOptions}
            getOptionLabel={(option) => option.label || ""}
            value={gaugeType}
            onChange={(e, newValue) => setGaugeType(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Gauge Type *" variant="outlined" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <TextField
            label="Standard Specification *"
            variant="outlined"
            size="small"
            fullWidth
            value={specification}
            onChange={(e) => setSpecification(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Limits Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 1 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#DDDDDD" }}>
            <TableRow>
              <TableCell sx={{ width: "22%" }}>
                <b>PARAMETER (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "26%" }}>
                <b>SPECIFIED LIMIT MAX (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "26%" }}>
                <b>SPECIFIED LIMIT MIN (MM)</b>
              </TableCell>
              <TableCell align="center" sx={{ width: "26%" }}>
                <b>WEAR LIMIT (MM)</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>Go</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goMax}
                  onChange={(e) => setGoMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goMin}
                  onChange={(e) => setGoMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={goWearLimit}
                  onChange={(e) => setGoWearLimit(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: "600" }}>No Go</TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoMax}
                  onChange={(e) => setNogoMax(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoMin}
                  onChange={(e) => setNogoMin(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
              <TableCell align="center">
                <TextField
                  type="number"
                  size="small"
                  value={nogoWearLimit}
                  onChange={(e) => setNogoWearLimit(e.target.value)}
                  sx={{ width: "80%" }}
                  inputProps={{ step: "any" }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Toolbar */}
      <Toolbar
        style={{
          padding: "0px",
          overflow: "auto",
          display: "flex",
          justifyContent: "flex-end",
          gap: "20px",
          marginTop: "3rem",
        }}
      >
        <Button
          variant="contained"
          style={{ backgroundColor: "grey" }}
          size="small"
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={handleSubmit}
        >
          {isEditing ? "Update" : "Save"}
        </Button>
      </Toolbar>
      <ToastContainer />
    </EditWrap>
  );
}